import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDate, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { InjuredParty, OshClassification, OshIncident, OshInvestigationStatus } from '../../lib/types';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Pagination, Spinner, StatusBadge, Tabs } from '../../ui/components';

const CLASSIFICATIONS: OshClassification[] = ['FATALITY', 'SPECIFIED_INJURY', 'OVER_7_DAY_INCAPACITY', 'DANGEROUS_OCCURRENCE', 'OCCUPATIONAL_DISEASE'];
const INVESTIGATION_STATUSES: OshInvestigationStatus[] = ['REPORTED', 'UNDER_INVESTIGATION', 'CLOSED'];

const TAB_LABEL = { report: 'Report incident', register: 'Register' } as const;
type TabKey = keyof typeof TAB_LABEL;

export function OshIncidentsHome() {
  const { persona } = useAuth();
  const isInspectionStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_INSPECTOR, M16_ROLES.CHIEF_INSPECTOR);
  const canReport = persona?.kind === 'employer' || isInspectionStaff;
  const tabs: TabKey[] = [...((canReport ? ['report'] : []) as TabKey[]), ...((isInspectionStaff ? ['register'] : []) as TabKey[])];
  const [tab, setTab] = useState<TabKey>(isInspectionStaff ? 'register' : 'report');

  return (
    <div>
      <PageHeader title="OSH Incidents" subtitle="Statutory RIDDOR-style occupational safety &amp; health incident reporting and investigation." />
      {tabs.length === 0 ? (
        <InfoNote message="Reporting requires an employer mandate (X-Acting-For), or a Labour Inspector / Chief Inspector filing on the employer's behalf." />
      ) : (
        <>
          <Tabs tabs={tabs.map((k) => ({ key: k, label: TAB_LABEL[k] }))} value={tab} onChange={setTab} />
          {tab === 'report' ? <ReportTab /> : null}
          {tab === 'register' ? <RegisterTab /> : null}
        </>
      )}
    </div>
  );
}

function ReportTab() {
  const { persona } = useAuth();
  const isInspectionStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_INSPECTOR, M16_ROLES.CHIEF_INSPECTOR);
  const [employerEntityId, setEmployerEntityId] = useState('');
  const [classification, setClassification] = useState<OshClassification | ''>('');
  const [incidentAt, setIncidentAt] = useState('');
  const [narrative, setNarrative] = useState('');
  const [injuredParties, setInjuredParties] = useState<InjuredParty[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<OshIncident | null>(null);

  const submit = async () => {
    if (!classification || !incidentAt || narrative.trim().length < 10) return;
    if (isInspectionStaff && !employerEntityId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        classification,
        incidentAt: new Date(incidentAt).toISOString(),
        narrative: narrative.trim(),
        injuredParties: injuredParties.filter((p) => p.fullName.trim() && p.natureOfInjury.trim()),
      };
      if (employerEntityId.trim()) body.employerEntityId = employerEntityId.trim();
      const res = await api.post<OshIncident>('/api/v1/m16/osh-incidents', body, {
        actingFor: persona?.kind === 'employer' ? persona.actingForBusinessId : undefined,
        idempotencyKey: newIdempotencyKey(),
      });
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Report failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="card max-w-xl border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-sm font-bold text-emerald-800">Incident reported — {created.refNo}</h2>
        <p className="mt-1 text-xs text-emerald-700">
          {created.withinStatutoryWindow ? 'Reported within the 24h statutory window.' : 'Reported outside the 24h statutory window — flagged, still accepted.'}
        </p>
        <div className="mt-3 flex gap-2">
          <Link to={`/osh-incidents/${created.id}`} className="btn-primary">
            View incident
          </Link>
          <button className="btn-secondary !bg-white" onClick={() => setCreated(null)}>
            Report another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card max-w-xl p-5">
      {persona?.kind === 'employer' ? (
        <p className="mb-3 text-xs text-slate-500">Reported for {persona.actingForBusinessId} via your employer mandate.</p>
      ) : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        {isInspectionStaff ? (
          <Field label="Employer entity ID">
            <input className="field-input" value={employerEntityId} onChange={(e) => setEmployerEntityId(e.target.value)} placeholder="e.g. BIZ-SC-200102" />
          </Field>
        ) : null}
        <Field label="Classification">
          <select className="field-input" value={classification} onChange={(e) => setClassification(e.target.value as OshClassification)}>
            <option value="">Choose…</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Incident date &amp; time">
          <input type="datetime-local" className="field-input" value={incidentAt} onChange={(e) => setIncidentAt(e.target.value)} />
        </Field>
        <Field label="Narrative" hint="At least 10 characters.">
          <textarea className="field-input min-h-[5rem]" value={narrative} onChange={(e) => setNarrative(e.target.value)} />
        </Field>

        <div>
          <div className="field-label">Injured parties (optional)</div>
          {injuredParties.map((p, i) => (
            <div key={i} className="mb-1.5 rounded-lg border border-slate-200 p-2">
              <div className="grid grid-cols-2 gap-1.5">
                <input
                  className="field-input !py-1 text-xs"
                  placeholder="Full name"
                  value={p.fullName}
                  onChange={(e) => setInjuredParties((rows) => rows.map((r, j) => (j === i ? { ...r, fullName: e.target.value } : r)))}
                />
                <input
                  className="field-input !py-1 text-xs"
                  placeholder="NIN (optional)"
                  value={p.nin ?? ''}
                  onChange={(e) => setInjuredParties((rows) => rows.map((r, j) => (j === i ? { ...r, nin: e.target.value } : r)))}
                />
              </div>
              <input
                className="field-input !py-1 mt-1.5 text-xs"
                placeholder="Nature of injury"
                value={p.natureOfInjury}
                onChange={(e) => setInjuredParties((rows) => rows.map((r, j) => (j === i ? { ...r, natureOfInjury: e.target.value } : r)))}
              />
              <button className="mt-1 text-[11px] font-semibold text-rose-600" onClick={() => setInjuredParties((rows) => rows.filter((_, j) => j !== i))}>
                Remove
              </button>
            </div>
          ))}
          <button className="btn-secondary w-full !py-1 text-xs" onClick={() => setInjuredParties((rows) => [...rows, { fullName: '', natureOfInjury: '' }])}>
            + Add injured party
          </button>
        </div>

        <button
          className="btn-primary w-full"
          onClick={submit}
          disabled={busy || !classification || !incidentAt || narrative.trim().length < 10 || (isInspectionStaff && !employerEntityId.trim())}
        >
          {busy ? 'Reporting…' : 'Report incident'}
        </button>
      </div>
    </div>
  );
}

function RegisterTab() {
  const [classification, setClassification] = useState('');
  const [status, setStatus] = useState('');
  const [employerEntityId, setEmployerEntityId] = useState('');
  const query = new URLSearchParams();
  if (classification) query.set('classification', classification);
  if (status) query.set('status', status);
  if (employerEntityId) query.set('employerEntityId', employerEntityId);
  const path = `/api/v1/m16/osh-incidents${query.toString() ? `?${query.toString()}` : ''}`;
  const listQ = useCursorList<OshIncident>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Classification" className="!mb-0">
          <select className="field-input" value={classification} onChange={(e) => setClassification(e.target.value)}>
            <option value="">All</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Investigation status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {INVESTIGATION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Employer entity ID" className="!mb-0">
          <input className="field-input" value={employerEntityId} onChange={(e) => setEmployerEntityId(e.target.value)} placeholder="BIZ-SC-…" />
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No OSH incidents match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Classification</th>
                <th className="table-head">Employer</th>
                <th className="table-head">Incident</th>
                <th className="table-head">24h window</th>
                <th className="table-head">Investigation</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/osh-incidents/${o.id}`}>
                      {o.refNo}
                    </Link>
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={o.classification} />
                  </td>
                  <td className="table-cell font-mono text-xs text-slate-500">{o.employerEntityId}</td>
                  <td className="table-cell text-xs">{formatDate(o.incidentAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.withinStatutoryWindow ? 'OK' : 'OVERDUE'} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={o.investigationStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}
